import { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { isApiActionSuccess } from "helpers/otpCooldown"
import { sendSensitiveActionOtp } from "store/actions/sensitiveAction"
import { showAlert } from "store/reducers/alertReducer"

const useSensitiveAction = ({ module, onSubmitWithOtp }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [sendingOtp, setSendingOtp] = useState(false)
  const [pendingPayload, setPendingPayload] = useState(null)

  const closeModal = useCallback(() => {
    setIsOpen(false)
    setPendingPayload(null)
    setConfirming(false)
    setSendingOtp(false)
  }, [])

  const isActionBusy = sendingOtp || isOpen || confirming

  const handleOtpSendResponse = useCallback(
    (action, { openModalOnSuccess = false } = {}) => {
      const { statusCode, status, msg } = action?.payload || {}
      if (statusCode === 200 && status) {
        dispatch(showAlert({ text: msg, type: "success" }))
        if (openModalOnSuccess) setIsOpen(true)
        return true
      }

      dispatch(
        showAlert({
          text: msg || t("Unable to send OTP. Please try again."),
          type: "error",
        })
      )
      return false
    },
    [dispatch, t]
  )

  const startSensitiveAction = useCallback(
    async payload => {
      if (sendingOtp || isOpen || confirming) return
      setPendingPayload(payload)
      setSendingOtp(true)
      try {
        const action = await dispatch(sendSensitiveActionOtp({ module }))
        handleOtpSendResponse(action, { openModalOnSuccess: true })
      } finally {
        setSendingOtp(false)
      }
    },
    [confirming, dispatch, handleOtpSendResponse, isOpen, module, sendingOtp]
  )

  const confirmOtp = useCallback(
    async otp => {
      if (!pendingPayload) return
      setConfirming(true)
      try {
        const result = await onSubmitWithOtp(pendingPayload, otp)
        if (isApiActionSuccess(result)) {
          closeModal()
        }
      } catch (error) {
        // Keep the OTP modal open so the user can retry.
      } finally {
        setConfirming(false)
      }
    },
    [closeModal, onSubmitWithOtp, pendingPayload]
  )

  return {
    startSensitiveAction,
    confirmOtp,
    isActionBusy,
    sendingOtp,
    modalProps: {
      isOpen,
      toggle: closeModal,
      onConfirm: confirmOtp,
      confirming,
    },
  }
}

export default useSensitiveAction
